import { CommonModule, DatePipe } from "@angular/common";
import { Component, inject } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";

import {
  DatosDetalleIso,
  DetalleIncidenciaIsoBase,
  TextosDetalleIso,
} from "../../shared/incidencias-iso/detalle-incidencia-iso-base";
import { EstadoSiguienteIso, etiquetaOpcion, porquesDetalleIso } from "../../shared/incidencias-iso/incidencias-iso.comun";
import {
  EstadoIncidenciaSeguridad,
  IncidenciaSeguridad,
  IncidenciaSeguridadPorques,
} from "../../shared/model/iso42001/incidencia-seguridad.model";
import {
  GRAVEDAD_OPTIONS,
  INCIDENCIA_SEGURIDAD_COLUMNS,
  PRIORIDAD_OPTIONS,
  TIPO_EVENTO_OPTIONS,
} from "../incidencias-seguridad.constants";
import { IncidenciasSeguridadService } from "../incidencias-seguridad.service";
import { RegresarProcesoSeguridadDialogComponent } from "../regresar-proceso-seguridad-dialog/regresar-proceso-seguridad-dialog.component";

export type DetalleIncidenciaSeguridadData = DatosDetalleIso<IncidenciaSeguridad>;

@Component({
  selector: "app-detalle-incidencia-seguridad",
  standalone: true,
  templateUrl: "./detalle-incidencia-seguridad.component.html",
  styleUrls: ["./detalle-incidencia-seguridad.component.css"],
  imports: [CommonModule, DatePipe, MatButtonModule, MatDialogModule, MatIconModule],
})
export class DetalleIncidenciaSeguridadComponent extends DetalleIncidenciaIsoBase<IncidenciaSeguridad> {
  readonly tipoEventoOptions = TIPO_EVENTO_OPTIONS;
  readonly gravedadOptions = GRAVEDAD_OPTIONS;
  readonly prioridadOptions = PRIORIDAD_OPTIONS;

  protected readonly incidenciasService = inject(IncidenciasSeguridadService);
  protected readonly columnasEstado = INCIDENCIA_SEGURIDAD_COLUMNS;
  protected readonly perfilAsignador = "Encargado incidencias seguridad";
  protected readonly reabrirSeccionAlCargar = true;
  protected readonly textos: TextosDetalleIso = {
    sinIdentificador: "No se encontró el identificador de la incidencia de seguridad.",
    errorCarga: "No fue posible cargar la incidencia de seguridad.",
    noEncontrada: "No se encontró la incidencia de seguridad.",
    soloAsignador: "Solo el encargado de incidencias de seguridad puede asignar el ticket.",
    soloRevisor: "Solo el responsable de seguimiento puede concluir o regresar la incidencia.",
    soloResponsable: "Solo el responsable de resolución asignado puede continuar la incidencia.",
    servicioNoEncontrado:
      "El backend no encontró el servicio de incidencias de seguridad. Revisa que CosRest esté compilado y reiniciado.",
    respuestaNoJson:
      "El backend respondió una página en lugar de JSON. La ruta de incidencias de seguridad no está llegando al controller.",
  };
  private readonly dialog = inject(MatDialog);

  editar(): void {
    if (!this.incidencia?.idIncidenciaIa || !this.puedeEditar()) {
      return;
    }
    this.dialogRef.close({
      action: this.incidencia.estadoEvento === "PROCESO" ? "seguimiento" : "edicion",
      refresh: this.requiereRefresh,
      incidencia: this.incidencia,
    });
  }

  cerrarDetalle(): void {
    this.dialogRef.close({ refresh: this.requiereRefresh });
  }

  regresarAProceso(): void {
    if (!this.incidencia?.idIncidenciaIa || !this.puedeRegresarAProceso()) {
      this.error = "Solo el responsable de seguimiento puede regresar la incidencia a proceso.";
      return;
    }
    this.pedirComentario("regresar", (comentario) => this.guardarComentarioYRegresarAProceso(comentario));
  }

  cancelarIncidencia(): void {
    if (!this.incidencia?.idIncidenciaIa || !this.puedeCancelar()) {
      this.error = "Solo el encargado de incidencias de seguridad puede cancelar incidencias abiertas o en proceso.";
      return;
    }
    this.pedirComentario("cancelar", (comentario) => this.enviarCancelacion(comentario));
  }

  puedeEditar(): boolean {
    if (!this.incidencia || this.incidencia.estadoEvento === "VOBO" || this.incidencia.estadoEvento === "CANCELADO") {
      return false;
    }
    if (this.incidencia.estadoEvento === "ABIERTO") {
      return this.esUsuarioAlta();
    }
    if (this.incidencia.estadoEvento === "PROCESO") {
      return this.incidencia.idUsuarioResponsableResolucion === this.idUsuarioSesion;
    }
    if (this.incidencia.estadoEvento === "ATENDIDO") {
      return false;
    }
    return true;
  }

  puedeCambiarEstado(): boolean {
    if (!this.incidencia || !this.siguienteEstado()) {
      return false;
    }
    if (this.incidencia.estadoEvento === "ABIERTO") {
      return this.esAsignador;
    }
    if (this.incidencia.estadoEvento === "PROCESO") {
      return this.incidencia.idUsuarioResponsableResolucion === this.idUsuarioSesion;
    }
    if (this.incidencia.estadoEvento === "ATENDIDO") {
      return this.incidencia.idUsuarioResponsableSeguimiento === this.idUsuarioSesion;
    }
    return false;
  }

  puedeRegresarAProceso(): boolean {
    return this.incidencia?.estadoEvento === "ATENDIDO"
      && this.incidencia.idUsuarioResponsableSeguimiento === this.idUsuarioSesion;
  }

  puedeCancelar(): boolean {
    return !!this.incidencia
      && this.esAsignador
      && (this.incidencia.estadoEvento === "ABIERTO" || this.incidencia.estadoEvento === "PROCESO");
  }

  mensajeCambioEstado(): string {
    if (this.incidencia?.estadoEvento === "CANCELADO") {
      return "Incidencia cancelada";
    }
    const siguiente = this.siguienteEstado();
    if (siguiente === "ATENDIDO") {
      return "Marcando incidencia como atendida...";
    }
    if (siguiente === "VOBO") {
      return "Enviando incidencia a Vo.Bo....";
    }
    if (siguiente === "PROCESO") {
      return "Regresando incidencia a proceso...";
    }
    return "Actualizando incidencia...";
  }

  labelTipo(value?: string): string {
    return etiquetaOpcion(value, this.tipoEventoOptions);
  }

  /** Pide el comentario del regreso a proceso o de la cancelación y, si se capturó, sigue con `alConfirmar`. */
  private pedirComentario(accion: "regresar" | "cancelar", alConfirmar: (comentario: string) => void): void {
    const ref = this.dialog.open(RegresarProcesoSeguridadDialogComponent, {
      disableClose: false,
      autoFocus: true,
      width: "80vw",
      maxWidth: "80vw",
      maxHeight: "75vh",
      panelClass: ["custom-modalbox", "incidencia-seguridad-dialog", "incidencia-seguridad-regresar-dialog"],
      data: {
        incidencia: this.incidencia,
        accion,
      },
    });
    ref.afterClosed().subscribe((result) => {
      if (!result?.confirmed || !result?.comentario?.trim()) {
        return;
      }
      alConfirmar(result.comentario.trim());
    });
  }

  private enviarCancelacion(comentario: string): void {
    if (!this.incidencia?.idIncidenciaIa) {
      return;
    }
    this.cambiandoEstado = true;
    this.error = "";
    this.advertencia = "";
    this.incidenciasService
      .cambiarEstado(this.incidencia.idIncidenciaIa, "CANCELADO", undefined, {
        idIncidenciaIa: this.incidencia.idIncidenciaIa,
        comentario,
      })
      .subscribe({
        next: (res) => {
          if (!res || res.codigoError) {
            this.error = res?.mensajeError || "No fue posible cancelar la incidencia.";
            this.cambiandoEstado = false;
            return;
          }
          this.requiereRefresh = true;
          this.cambiandoEstado = false;
          this.dialogRef.close({ refresh: true, changedState: "CANCELADO" });
        },
        error: (e) => {
          this.error = e?.error?.mensajeError || e?.error?.message || "No fue posible cancelar la incidencia.";
          this.cambiandoEstado = false;
        },
      });
  }

  /** Seguridad cierra el detalle después de cambiar el estado (al marcar atendido envía la incidencia). */
  protected enviarCambioEstado(siguiente: EstadoSiguienteIso): void {
    if (!this.incidencia?.idIncidenciaIa) {
      return;
    }
    const incidenciaCambio = siguiente === "ATENDIDO" ? this.incidencia : undefined;
    this.solicitarCambioEstado(siguiente, incidenciaCambio, () => this.dialogRef.close({ refresh: true, changedState: siguiente }));
  }

  protected seccionDefaultPorEstado(estado?: EstadoIncidenciaSeguridad): string {
    if (estado === "PROCESO" || estado === "ATENDIDO") {
      return "seguimiento";
    }
    if (estado === "VOBO" || estado === "CANCELADO") {
      return "";
    }
    return "resumen";
  }

  protected faltantesParaAtendido(): string[] {
    const faltantes: string[] = [];
    if (!this.incidencia?.fechaPlaneadaResolucion) {
      faltantes.push("Fecha planeada de resolución");
    }
    if (!this.incidencia?.accionCorrectiva?.trim()) {
      faltantes.push("Acción correctiva");
    }
    if (!this.incidencia?.tipoSolucion?.trim()) {
      faltantes.push("Tipo de solución");
    }
    if (!this.incidencia?.idEmpleadoResponsableSeguimiento) {
      faltantes.push("Responsable de seguimiento");
    }
    const porques = porquesDetalleIso(this.incidencia?.causaOrigen);
    for (let i = 1; i <= 5; i++) {
      if (!porques[String(i)]?.trim()) {
        faltantes.push(`5 porqués: respuesta ${i}`);
      }
    }
    if (!this.comentarios.length) {
      faltantes.push("Al menos un comentario");
    }
    return faltantes;
  }

  protected preguntaPorque(numero: number, porques: IncidenciaSeguridadPorques): string {
    if (numero === 1) {
      return `¿Por qué se presentó ${this.labelTipo(this.incidencia?.tipoIncidencia) || "el evento"}?`;
    }
    const anterior = this.quitarPrefijoPorque(porques[String(numero - 1)]) || `la respuesta ${numero - 1}`;
    return `¿Por qué ${anterior}?`;
  }

  private quitarPrefijoPorque(value?: string): string {
    return (value || "").trim().replace(/^(por\s+que|porque)\s+/i, "").trim();
  }
}
